import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';

interface NetworkInformation {
  downlink: number;        // Mbps estimados
  effectiveType: string;   // 'slow-2g' | '2g' | '3g' | '4g'
  rtt: number;             // Round-trip time en ms
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

@Injectable({
  providedIn: 'root'
})
export class NetworkCheckService {

  // Umbrales para plataforma transaccional financiera
  private readonly MIN_DOWNLINK_MBPS = 1.5;
  private readonly MAX_RTT_MS = 1500;
  private readonly BLOCKED_TYPES = ['slow-2g', '2g'];
  private readonly CHECK_INTERVAL_MS = 600000; // 10 minutos

  private warningVisible = false;
  private intervalId: any = null;

  /**
   * Inicia el monitoreo de conexión.
   * Verifica inmediatamente y luego cada 60 segundos.
   * También escucha cambios de conexión en tiempo real.
   */
  startMonitoring(): void {
    this.checkConnection();

    this.intervalId = setInterval(() => {
      this.checkConnection();
    }, this.CHECK_INTERVAL_MS);

    // Escuchar cambios de red en tiempo real
    const connection = this.getConnection();
    if (connection) {
      connection.addEventListener('change', () => this.checkConnection());
    }

    // Escuchar cuando el usuario pierde/recupera conexión
    window.addEventListener('offline', () => this.showOfflineWarning());
    window.addEventListener('online', () => this.checkConnection());
  }

  stopMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private getConnection(): NetworkInformation | null {
    const nav = navigator as any;
    return nav.connection || nav.mozConnection || nav.webkitConnection || null;
  }

  private checkConnection(): void {
    // Si está offline, mostrar warning de offline
    if (!navigator.onLine) {
      this.showOfflineWarning();
      return;
    }

    const connection = this.getConnection();
    if (!connection) return; // Navegador no soporta Network Information API

    const { downlink, effectiveType, rtt } = connection;

    const isSlowType = this.BLOCKED_TYPES.includes(effectiveType);
    const isLowBandwidth = downlink < this.MIN_DOWNLINK_MBPS;
    const isHighLatency = rtt > this.MAX_RTT_MS;

    if (isSlowType || (isLowBandwidth && isHighLatency)) {
      this.showSlowConnectionWarning(downlink, effectiveType, rtt);
    }
  }

  private showSlowConnectionWarning(downlink: number, effectiveType: string, rtt: number): void {
    if (this.warningVisible) return;
    this.warningVisible = true;

    const speedLabel = this.getSpeedLabel(effectiveType);
    const details = `Velocidad: ${downlink} Mbps | Latencia: ${rtt}ms | Red: ${effectiveType.toUpperCase()}`;

    Swal.fire({
      icon: 'warning',
      title: 'Conexi\u00f3n inestable',
      html: `
        <div style="text-align: center; font-family: 'Poppins', sans-serif;">
          <p style="color: #6b7280; font-size: 0.9rem; margin-bottom: 0.75rem;">
            Tu conexi\u00f3n a internet es <strong style="color: #92400e;">${speedLabel}</strong>.
          </p>
          <p style="color: #6b7280; font-size: 0.9rem; margin-bottom: 1rem;">
            Te recomendamos mejorar tu conexi\u00f3n antes de realizar transacciones.
          </p>
          <div style="background: #fef3c7; border-radius: 8px; padding: 0.5rem 0.75rem; font-size: 0.75rem; color: #92400e;">
            ${details}
          </div>
        </div>
      `,
      confirmButtonText: 'Entendido',
      allowOutsideClick: true,
      customClass: {
        popup: 'aguacate-swal-popup',
        confirmButton: 'aguacate-swal-confirm',
      },
      buttonsStyling: false,
      didOpen: () => {
        const btn = Swal.getConfirmButton();
        if (btn) btn.style.color = '#ffffff';
      },
    }).then(() => {
      this.warningVisible = false;
    });
  }

  private showOfflineWarning(): void {
    if (this.warningVisible) return;
    this.warningVisible = true;

    Swal.fire({
      icon: 'error',
      title: 'Sin conexi\u00f3n',
      html: `
        <div style="text-align: center; font-family: 'Poppins', sans-serif;">
          <p style="color: #6b7280; font-size: 0.9rem;">
            No tienes conexi\u00f3n a internet.
            No realices operaciones hasta recuperar la conexi\u00f3n.
          </p>
        </div>
      `,
      confirmButtonText: 'Entendido',
      allowOutsideClick: false,
      customClass: {
        popup: 'aguacate-swal-popup',
        confirmButton: 'aguacate-swal-confirm',
      },
      buttonsStyling: false,
      didOpen: () => {
        const btn = Swal.getConfirmButton();
        if (btn) btn.style.color = '#ffffff';
      },
    }).then(() => {
      this.warningVisible = false;
    });
  }

  private getSpeedLabel(effectiveType: string): string {
    switch (effectiveType) {
      case 'slow-2g': return 'muy lenta';
      case '2g': return 'lenta';
      case '3g': return 'baja';
      default: return 'inestable';
    }
  }
}
